/** 
  Author: Lokesh Arora - lokesh.arora@enabledata.com
  Purpose: Layout routes
  Date: April 2020
**/

/**
 * @desc Attaches a layout to a given route and checks for token in local storage
 */
import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import './baseStyle.css';
import LocalStorageService from '../services/localStorage';
import IdleTracker from '../components/IdleTracker'

import {
  Paper,
  Dialog,
  Typography,
  IconButton,
  DialogContent,
  withStyles,
  Tooltip
} from '@material-ui/core';
import { ChangePasswordPage } from '../pages';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';

const customStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'white',
  },
  backDrop: {
    backdropFilter: "blur(3px)",
    backgroundColor: 'rgba(0,0,30,0.4)'
  },
});

const DialogTitle = withStyles(customStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" style={{ color: 'white' }}>{children}</Typography>
      {onClose ? (
        <Tooltip title="Logout">
        <IconButton aria-label="close" className={classes.closeButton}  onClick={onClose}>
          <PowerSettingsNewIcon />
        </IconButton>
        </Tooltip>
      ) : null}
    </MuiDialogTitle>
  );
});

const LayoutRoute = ({
  page: Component,
  layout: Layout,
  requireAuth,
  activeIcon,
  ...rest
}) => {
  const localStorageService = LocalStorageService.getService();

  // Get user info from local storage
  const token = localStorageService.getAccessToken();
  const loginAction = useStoreActions((actions) => actions.login);
  const subHeaderAction = useStoreActions((actions) => actions.subHeader);
  let userInfo = localStorageService.getUserInfo();
  const [pwdChange, setPwdChange] = React.useState(false);
  // Check if token available for a restricted route, if not redirect to login page
  // else redirect to landing page
  if (requireAuth && token === null) {
    return <Redirect to={{ pathname: '/login' }} />;
  }
  if (requireAuth === false && token !== null) {
    return <Redirect to={{ pathname: '/' }} />;
  }
  // if force_change_password is true, then redirect to resetPassword page
  if (userInfo != null && userInfo != undefined) {
    userInfo = JSON.parse(userInfo);

    if (userInfo.force_change_password) {
      if (!pwdChange)
        setPwdChange(true);
    }
  }

  const logout = (e, reason ) => {
    if (reason === "backdropClick")
      return false;
    loginAction.logout();
    window.location = '/';
  };

  return (
    <>
      {userInfo && userInfo !== null && (!userInfo.is_sso_user || (userInfo.is_sso_user && userInfo.is_sso_user?.toUpperCase() == 'N')) && (<IdleTracker />)}
      <Dialog
        open={pwdChange}
        fullWidth
        maxWidth="xs"
        onClose={logout}
        className="blur"
        disableEscapeKeyDown
        backdropClick
      >
        <DialogTitle onClose={logout}>
          Change Password
        </DialogTitle>
        <DialogContent>
          <Paper square>
            <ChangePasswordPage />
          </Paper>
        </DialogContent>
      </Dialog>
      <Route
        {...rest}
        render={(props) => {
          // Sub header only for policy page
          if (props.match.url.indexOf('policy') > -1 && props.match.params.id) {
            subHeaderAction.setSubHeader(true);
          } else {
            subHeaderAction.setSubHeader(false);
          }
          if (Layout) {
            return (
              <Layout activeMenu={activeIcon}>
                <Component {...props} />
              </Layout>
            );
          }
          return <Component {...props} />;
        }}
      />
    </>
  );
};

export default LayoutRoute;
